import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  DialogoDeConfirmacao,
  type TomDoConfirmar,
} from "@base/ui";
import {
  avisarComoFallback,
  confirmarComoFallback,
  ligarDialogosDoApp,
} from "./dialogos-api";

export type PedidoDeAviso = {
  readonly titulo: string;
  readonly descricao: string;
  readonly rotuloConfirmar?: string;
};

export type PedidoDeConfirmacao = {
  readonly titulo: string;
  readonly descricao: string;
  readonly rotuloConfirmar?: string;
  readonly rotuloCancelar?: string;
  readonly tomConfirmar?: TomDoConfirmar;
  /** Se definido, o usuario digita este texto para habilitar Confirmar. */
  readonly textoParaConfirmar?: string;
};

type EstadoAviso = PedidoDeAviso & {
  readonly tipo: "aviso";
  readonly resolver: () => void;
};

type EstadoConfirmacao = PedidoDeConfirmacao & {
  readonly tipo: "confirmacao";
  readonly resolver: (ok: boolean) => void;
};

type EstadoDoDialogo = EstadoAviso | EstadoConfirmacao | null;

type ApiDeDialogos = {
  readonly avisar: (pedido: PedidoDeAviso) => Promise<void>;
  readonly confirmar: (pedido: PedidoDeConfirmacao) => Promise<boolean>;
};

const Contexto = createContext<ApiDeDialogos | null>(null);

/**
 * Provedor global de aviso/confirmacao (DialogoDeConfirmacao).
 * Tambem registra a API imperativa para funcoes fora de React.
 */
export function ProvedorDeDialogos({
  children,
}: {
  readonly children: ReactNode;
}) {
  const [estado, definirEstado] = useState<EstadoDoDialogo>(null);
  const fila = useRef(Promise.resolve());

  const avisar = useCallback((pedido: PedidoDeAviso) => {
    return new Promise<void>((resolver) => {
      fila.current = fila.current.then(
        () =>
          new Promise<void>((liberarFila) => {
            definirEstado({
              tipo: "aviso",
              ...pedido,
              resolver: () => {
                resolver();
                liberarFila();
              },
            });
          }),
      );
    });
  }, []);

  const confirmar = useCallback((pedido: PedidoDeConfirmacao) => {
    return new Promise<boolean>((resolver) => {
      fila.current = fila.current.then(
        () =>
          new Promise<void>((liberarFila) => {
            definirEstado({
              tipo: "confirmacao",
              ...pedido,
              resolver: (ok) => {
                resolver(ok);
                liberarFila();
              },
            });
          }),
      );
    });
  }, []);

  const api = useMemo(() => ({ avisar, confirmar }), [avisar, confirmar]);

  useEffect(() => {
    ligarDialogosDoApp(api);
    return () => ligarDialogosDoApp(null);
  }, [api]);

  return (
    <Contexto.Provider value={api}>
      {children}
      {estado?.tipo === "aviso" ? (
        <DialogoDeConfirmacao
          aberto
          modo="aviso"
          titulo={estado.titulo}
          descricao={estado.descricao}
          {...(estado.rotuloConfirmar
            ? { rotuloConfirmar: estado.rotuloConfirmar }
            : {})}
          aoConfirmar={() => {
            const resolver = estado.resolver;
            definirEstado(null);
            resolver();
          }}
          aoCancelar={() => {
            const resolver = estado.resolver;
            definirEstado(null);
            resolver();
          }}
        />
      ) : null}
      {estado?.tipo === "confirmacao" ? (
        <DialogoDeConfirmacao
          aberto
          modo="confirmacao"
          titulo={estado.titulo}
          descricao={estado.descricao}
          tomConfirmar={estado.tomConfirmar ?? "perigo"}
          {...(estado.rotuloConfirmar
            ? { rotuloConfirmar: estado.rotuloConfirmar }
            : {})}
          {...(estado.rotuloCancelar
            ? { rotuloCancelar: estado.rotuloCancelar }
            : {})}
          {...(estado.textoParaConfirmar
            ? { textoParaConfirmar: estado.textoParaConfirmar }
            : {})}
          aoConfirmar={() => {
            const resolver = estado.resolver;
            definirEstado(null);
            resolver(true);
          }}
          aoCancelar={() => {
            const resolver = estado.resolver;
            definirEstado(null);
            resolver(false);
          }}
        />
      ) : null}
    </Contexto.Provider>
  );
}

export function useDialogos(): ApiDeDialogos {
  const ctx = useContext(Contexto);
  if (!ctx) {
    return {
      avisar: avisarComoFallback,
      confirmar: confirmarComoFallback,
    };
  }
  return ctx;
}
